/**
 * The client-side inverse of `snapshot.ts`: rebuild a `MahjongState` from a
 * per-seat-filtered `MahjongSnapshot`, so every existing renderer, hit-test and
 * the Coach panel keep consuming the exact type they already do. Online and
 * solo share one render path — the alternative (a separate `MahjongView` type)
 * would force retyping `SceneRender`, `HandRenderer`, `DiscardRenderer`,
 * `hitTest`, `findHoveredTile` and `Coach` for no behavioral gain.
 *
 * ⚠️ **THE TILES THIS MODULE SYNTHESISES ARE NOT REAL TILES.** A filtered
 * snapshot carries opponents' hands and both walls as *counts only* — that is
 * the whole point of the hidden-info filter. To keep `MahjongState`'s shape,
 * those counts are re-inflated into arrays of PLACEHOLDER tiles. Only `.length`
 * may ever be read from them:
 *
 *   - never render a placeholder's face (`drawOpponentHandFaceUp` and friends),
 *   - never hit-test, name, or score one,
 *   - never treat its `id` as identifying a physical tile.
 *
 * Placeholders are deliberately easy to detect — every id is **negative**,
 * while every id in a real deal is `0 .. TILE_COUNT-1`. Use `isPlaceholderTile`
 * / `hasPlaceholderTiles` rather than re-deriving that rule.
 */

import { SEAT_COUNT, TILE_COUNT, type Tile, type TileKind } from '../game/types.js';
import type { MahjongState } from '../game/stateCore.js';
import { resolveRuleset, type MahjongSnapshot, type SeatHandView } from './snapshot.js';

/**
 * Placeholder ids run downwards from here. Real ids are non-negative
 * (`Wall.buildWall` numbers the 136 tiles `0 .. TILE_COUNT-1`), so the two
 * spaces can never overlap however the deal is shuffled.
 */
export const PLACEHOLDER_ID_BASE = -1;

/**
 * The identity every placeholder wears. White Dragon (Haku) is the blank-faced
 * tile of the shipped sprite set, so a placeholder that leaks into a face-up
 * draw path reads as conspicuously wrong rather than as a plausible hand — but
 * the identity carries no meaning and must never be interpreted.
 */
const PLACEHOLDER_KIND: TileKind = { kind: 'honor', honor: 'white' };

/** Disjoint id ranges, so no two synthesised tiles ever share an id. */
const WALL_POOL = SEAT_COUNT;
const DEAD_WALL_POOL = SEAT_COUNT + 1;

/**
 * `MahjongState.seed` is non-nullable, but the filter withholds the real seed
 * until `hand-end` (it reproduces the entire wall). Nothing on the client ever
 * re-deals from it — the server is authoritative — so this stands in until the
 * hand ends and the real seed arrives as the provenance record.
 */
export const SEED_WITHHELD = 0;

export function isPlaceholderTile(tile: Tile): boolean {
  return tile.id <= PLACEHOLDER_ID_BASE;
}

export function hasPlaceholderTiles(tiles: readonly Tile[]): boolean {
  return tiles.some(isPlaceholderTile);
}

/**
 * Rebuild the engine state a filtered snapshot describes. Pure, and it needs no
 * seat argument: every `{ kind: 'own' }` entry is rebuilt from its real tiles
 * and every `{ kind: 'hidden' }` one from placeholders, whichever seats those
 * happen to be. Mid-hand exactly one entry is `own` (the recipient's); at
 * `hand-end` all four are, because the filter reveals the whole table then.
 */
export function snapshotToState(snapshot: MahjongSnapshot): MahjongState {
  return {
    hands: snapshot.handsSeatView.map(handFromView),
    melds: snapshot.melds,
    discards: snapshot.discards,
    wall: placeholderTiles(snapshot.wallCount, WALL_POOL),
    deadWall: placeholderTiles(snapshot.deadWallCount, DEAD_WALL_POOL),
    turn: snapshot.turn,
    phase: snapshot.phase,
    lastDiscard: snapshot.lastDiscard,
    drawnTile: snapshot.drawnTile,
    result: snapshot.result,
    seed: snapshot.seed ?? SEED_WITHHELD,
    ruleset: resolveRuleset(snapshot.rulesetId),
    pendingAddedKongSeat: snapshot.pendingAddedKongSeat,
  };
}

function handFromView(view: SeatHandView, seat: number): readonly Tile[] {
  return view.kind === 'own' ? view.tiles : placeholderTiles(view.count, seat);
}

/**
 * `count` stand-in tiles for a pool whose composition the recipient may not
 * know. Ids are a pure function of `(pool, index)`, so repeated rebuilds of the
 * same snapshot produce identical tiles and nothing keyed by id churns.
 */
function placeholderTiles(count: number, pool: number): readonly Tile[] {
  return Array.from({ length: count }, (_, index) => ({
    ...PLACEHOLDER_KIND,
    id: PLACEHOLDER_ID_BASE - (pool * TILE_COUNT + index),
  }));
}
